import { format, isThisYear, isToday, isValid, isYesterday } from 'date-fns';

import { Anchor, Box, Divider, Text, TextButton } from '@zillow/constellation-10';

import { LoadingTag, LoadingText } from '@zg-rentals/loading-components';

import { getTagTypeByKey, STATUS_KEY } from '../../../../../srcLegacy/shared/utils/statusTagUtils';

import { lineClampStyles, singleTextLineStyles } from '../../../../../srcLegacy/shared/styles';

export type Lead = {
  conversationId: string;
  latestContact: {
    isPhoneLead: boolean;
    isRead: boolean;
    messageDateMs: string;
    messageText: string;
    notRepliedTo: boolean;
  };
  renterInfo: { renterPhoneNumber: string; renterName: string };
  status: { isConversationBlockedByZillow: boolean };
  statusLabel?: { linkId: string; text: string };
};

type Props = {
  lead: Lead;
  includeDivider: boolean;
  listingAlias: string;
  isLoading?: boolean;
};

const getMessageDate = (messageDateMs = ''): string => {
  if (!isValid(new Date(+messageDateMs))) return '';
  if (isYesterday(new Date(+messageDateMs))) return 'Yesterday';
  if (isToday(new Date(+messageDateMs))) return format(new Date(+messageDateMs), 'h:mm a');
  const formatToken = isThisYear(new Date(+messageDateMs)) ? 'MMM do' : 'MMM do, yyyy';
  return format(new Date(+messageDateMs), formatToken);
};

const LeadDetails = ({ lead, includeDivider, listingAlias, isLoading = false }: Props) => {
  const {
    conversationId,
    latestContact: { isPhoneLead, isRead, messageDateMs, messageText, notRepliedTo },
    renterInfo: { renterPhoneNumber, renterName },
    status: { isConversationBlockedByZillow },
    statusLabel: { linkId = '', text = '' } = {},
  } = lead;

  const renterNameDisplay =
    isPhoneLead && !renterName ? renterPhoneNumber || '(Phone lead)' : renterName || 'Name unavailable';
  const shouldShowNewTag = notRepliedTo && linkId !== 'statusResponseOverdue';
  const showUnread = !isRead && !isPhoneLead && !isLoading;
  const isTourRequestedLead = linkId === 'statusLabelTourRequested';

  return (
    <div data-testid="LeadDetails">
      <Box css={{ display: 'flex', justifyContent: 'space-between', marginBottom: '200' }}>
        <Box css={{ display: 'flex', flex: '1' }}>
          <LoadingText
            data-testid="Name"
            textStyle="body-bold"
            title={renterNameDisplay}
            isLoading={isLoading}
            loadingWidth="100%"
            css={{ marginRight: '200', ...singleTextLineStyles }}
            className="fs-mask"
          >
            {renterNameDisplay}
          </LoadingText>
        </Box>
        <LoadingText data-testid="Date" textStyle="body-xs" isLoading={isLoading} loadingWidth="20%">
          {getMessageDate(messageDateMs)}
        </LoadingText>
      </Box>

      <Box data-testid="TagContainer" css={{ display: 'flex', marginBottom: '200' }}>
        {Boolean(shouldShowNewTag || isLoading) && (
          <LoadingTag tone="aqua" size="sm" css={{ marginRight: '100' }} loadingWidth="20%" isLoading={isLoading}>
            NEW
          </LoadingTag>
        )}
        {Boolean(linkId || isLoading) && (
          <LoadingTag
            tone={isLoading ? 'gray' : getTagTypeByKey(STATUS_KEY.INTERESTED_RENTERS_LEAD_MANAGEMENT, linkId)}
            size="sm"
            css={{ marginRight: '100' }}
            loadingWidth="40%"
            isLoading={isLoading}
          >
            {text}
          </LoadingTag>
        )}
      </Box>

      <Box
        data-testid="MessageContainer"
        css={{
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: { base: isLoading ? 'row' : 'column', md: 'row' },
          gap: { base: '200', md: '400' },
        }}
      >
        <Box css={{ display: 'flex', flex: '1', minWidth: 0 }}>
          {showUnread && (
            <Box css={{ marginRight: '200' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                <circle cx="4" cy="4" r="4" fill="#006AFF" />
              </svg>
            </Box>
          )}
          {isConversationBlockedByZillow ? (
            <Text textStyle="body-sm" asChild>
              <p>
                Zillow has blocked this conversation because it was determined to be a violation of our Good Neighbor
                Policy.{' '}
                <Anchor
                  href="https://www.zillow.com/z/info/good-neighbor-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View policy
                </Anchor>
              </p>
            </Text>
          ) : (
            <>
              {isPhoneLead ? (
                isLoading ? (
                  <LoadingText isLoading={true} loadingWidth="100%">
                    <span />
                  </LoadingText>
                ) : (
                  <audio controls={true} style={{ display: 'block', width: '100%' }}>
                    <source src={messageText} />
                  </audio>
                )
              ) : (
                <LoadingText
                  textStyle="body-sm"
                  isLoading={isLoading}
                  loadingWidth="100%"
                  css={lineClampStyles}
                  className="fs-mask"
                >
                  {messageText}
                </LoadingText>
              )}
            </>
          )}
        </Box>

        {isTourRequestedLead && (
          <Box css={{ minWidth: '94px' }}>
            <TextButton data-testid="RespondNowTourBtn" asChild textStyle="body-sm-bold">
              <a href={`/rental-manager/lead-management/${listingAlias}?conversationId=${conversationId}`}>
                Respond now
              </a>
            </TextButton>
          </Box>
        )}
      </Box>

      {includeDivider && <Divider css={{ marginBlock: '600' }} />}
    </div>
  );
};

export default LeadDetails;
